import CLIENT from '../utils/restClient';
import Logger, { LOG_LEVELS, ALL_MESSAGES } from '../utils/logger';
import FileSaver from 'file-saver';
import _ from 'lodash';
import ENUMS, { FilingTypeEnum, FilingFileExtention } from '../utils/enums';
import getClientConfig from '../utils/getClientConfig';
import FilingHelper from '../components/filing/filingHelper';

export const MODULE_NAME = 'FILING_CONTROLLER';

const log = new Logger(MODULE_NAME, LOG_LEVELS.DEBUG);
const DelayTimeInterval = 150;
function namespaced(name) {
  return MODULE_NAME + '.' + name;
}

const { vrRestApiHost } = getClientConfig();

const ENABLE_ASYNC_VAT_RETURN_OPEN_RETURN_PAGE = 'EnableAsyncVatReturnOpenReturnPage';
const ENABLE_ASYNC_VAT_RETURN_FOR_MVR_USERS = 'EnableAsyncVatReturnsForMvrUsers';

const isMVRUser = () => Boolean(typeof localStorage !== 'undefined' && localStorage.mvrCustomerId);

// Returns the URL version segment to use for a country-specific VAT submit
// endpoint (Digipoort for NL, ERIC/Elster for DE):
//   - 'v6' when the async open-return-page flow is active for this user and
//          country (mirrors FilingPage.isAsyncVatReturnEnabled gating).
//   - ''   otherwise (the legacy unversioned/sync endpoint).
// "v1" is not a real version label; the legacy endpoint is simply unversioned.
//
// Async is active iff ALL of the following hold (kept in sync with
// FilingPage.isAsyncVatReturnEnabled so the submit endpoint matches whichever
// generation flow the user actually went through):
//   1. EnableAsyncVatReturnOpenReturnPage is on.
//   2. If the user is an MVR user, EnableAsyncVatReturnsForMvrUsers is also on.
//   3. The country is NOT listed in SYNCHRONOUS_ONLY_VAT_RETURN_COUNTRIES.
// Only VAT submissions are eligible for the v6 (async) endpoint. Non-VAT submissions
// (e.g. NL EC Listing) always use the legacy/sync endpoint regardless of feature flags.
// Callers must pass `reportType` explicitly so the intent is visible at every call site.
function resolveSubmissionVersion(featureFlag, countryCode, reportType) {
  if (reportType !== 'vat') {
    return '';
  }

  const asyncEnabled = Boolean(featureFlag?.[ENABLE_ASYNC_VAT_RETURN_OPEN_RETURN_PAGE]);
  if (!asyncEnabled) {
    return '';
  }

  if (isMVRUser() && !featureFlag?.[ENABLE_ASYNC_VAT_RETURN_FOR_MVR_USERS]) {
    return '';
  }

  const { synchronousOnlyVatReturnCountries } = getClientConfig();
  const syncOnlyCountries = synchronousOnlyVatReturnCountries
    ? synchronousOnlyVatReturnCountries.split(',').map((c) => c.trim()).filter(Boolean)
    : [];
  if (syncOnlyCountries.includes(countryCode)) {
    return '';
  }

  return 'v6';
}

//extract the InvoiceNr and LineNr from the concatenated string in response
function formatLineDataForIntrastatReport(listOfInvoiceNrLineNrPairs){
  var arrayOfInvoiceNrLineNrPair = listOfInvoiceNrLineNrPairs.split(",");
  var mapInvoiceNrToLineNrs = new Map();
  _.forEach(arrayOfInvoiceNrLineNrPair, function(pair) {
    var invoiceNrLineNrPair = pair.split("=");
    if(invoiceNrLineNrPair.length>1){
      var invoiceNr = invoiceNrLineNrPair[1];
      if(!mapInvoiceNrToLineNrs.has(invoiceNr)){
        mapInvoiceNrToLineNrs.set(invoiceNr, []);
      }
      mapInvoiceNrToLineNrs.get(invoiceNr).push(invoiceNrLineNrPair[0]); //an InvoiceNr can have multiple LineNrs, keep all of them
    }
  });
   return new Map([...mapInvoiceNrToLineNrs.entries()].sort()); //sort by InvoiceNr to maintain proper order in drilldown pagination
}

//extract and format InvoiceNr and LineNr from response for Intrstat Goods to display on UI
function formatResponseForIntrastatReport(filingValues){
  let goodsData = (filingValues.intrastat && filingValues.intrastat.goodsLines) || [];

  if(goodsData.length > 0)
  {
    _.forEach(goodsData, function(row) {
     var sortedInvoiceNrLineNr = formatLineDataForIntrastatReport(row.listOfInvoiceNrDocIDPairs);
     row.listOfInvoicesString =[ ...sortedInvoiceNrLineNr.keys() ].join(', ');
     row.lineNumbers = [ ...sortedInvoiceNrLineNr.values() ].flat();
    });
    filingValues.intrastat.goodsLines = goodsData;
  }

  return filingValues;
}

//Get LineIDs for current page for Intrstat drilldown
function getLineIDsForCurrentPage(lineNumbers, pagination) {
  let lineIDs = [];
  if (lineNumbers !== undefined) {
    lineIDs = lineNumbers.slice(pagination.startAt - 1, pagination.startAt + pagination.pageSize - 1);
  }
  return lineIDs;
}

const LOAD_VALUES = namespaced('LOAD_VALUES');
const UNLOAD_VALUES = namespaced('UNLOAD_VALUES');
const LOAD_VALUES_OK = namespaced('LOAD_VALUES_OK');
const LOAD_VALUES_FAIL = namespaced('LOAD_VALUES_FAIL');
const LOAD_VALUE_DETAILS = namespaced('LOAD_VALUE_DETAILS');
const LOAD_VALUE_DETAILS_OK = namespaced('LOAD_VALUE_DETAILS_OK');
const LOAD_VALUE_DETAILS_FAIL = namespaced('LOAD_VALUE_DETAILS_FAIL');
const EXPORT_VALUE_DETAILS = namespaced('EXPORT_VALUE_DETAILS');
const EXPORT_VALUE_DETAILS_OK = namespaced('EXPORT_VALUE_DETAILS_OK');
const EXPORT_VALUE_DETAILS_FAIL = namespaced('EXPORT_VALUE_DETAILS_FAIL');
const CREATE_FILING_DOCUMENT = namespaced('CREATE_FILING_DOCUMENT');
const CREATE_FILING_DOCUMENT_OK = namespaced('CREATE_FILING_DOCUMENT_OK');
const CREATE_FILING_DOCUMENT_FAIL = namespaced('CREATE_FILING_DOCUMENT_FAIL');
const CLEAR_FILING_DOCUMENT_DETAILS = namespaced('CLEAR_FILING_DOCUMENT_DETAILS');
const LOAD_EC_LISTING_DETAILS_OK = namespaced('LOAD_EC_LISTING_DETAILS_OK');
const LOAD_EC_LISTING_DETAILS_FAIL = namespaced('LOAD_EC_LISTING_DETAILS_FAIL');
const CLEAR_VALUE_DETAILS = namespaced('CLEAR_VALUE_DETAILS');
const LOAD_INTRA_DETAILS = namespaced('LOAD_INTRA_DETAILS');
const LOAD_INTRA_DETAILS_OK = namespaced('LOAD_INTRA_DETAILS_OK');
const LOAD_INTRA_DETAILS_FAIL = namespaced('LOAD_INTRA_DETAILS_FAIL');
const DOWNLOAD_FILING_DOCUMENT_OK = namespaced('DOWNLOAD_FILING_DOCUMENT_OK');
const DOWNLOAD_FILING_DOCUMENT_FAIL = namespaced('DOWNLOAD_FILING_DOCUMENT_FAIL');
const CHECK_VATNUMBER = namespaced('CHECK_VATNUMBER');
const CHECK_VATNUMBER_OK = namespaced('CHECK_VATNUMBER_OK');
const CHECK_VATNUMBER_FAIL = namespaced('CHECK_VATNUMBER_FAIL');
const CHECK_VATNUMBER_INPROGRESS = namespaced('CHECK_VATNUMBER_INPROGRESS');
const CHECK_VATNUMBER_DONE = namespaced('CHECK_VATNUMBER_DONE');
const RELIABILITY_CHECK_VATNUMBER = namespaced('RELIABILITY_CHECK_VATNUMBER');
const RELIABILITY_CHECK_VATNUMBER_OK = namespaced('RELIABILITY_CHECK_VATNUMBER_OK');
const RELIABILITY_CHECK_VATNUMBER_FAIL = namespaced('RELIABILITY_CHECK_VATNUMBER_FAIL');
const LOAD_DE_SUBMIT_REPORT = namespaced('LOAD_DE_SUBMIT_REPORT');
const LOAD_DE_SUBMIT_REPORT_OK = namespaced('LOAD_DE_SUBMIT_REPORT_OK');
const LOAD_DE_SUBMIT_REPORT_FAIL = namespaced('LOAD_DE_SUBMIT_REPORT_FAIL');
const LOAD_NL_SUBMIT_REPORT = namespaced('LOAD_NL_SUBMIT_REPORT');
const LOAD_NL_SUBMIT_REPORT_OK = namespaced('LOAD_NL_SUBMIT_REPORT_OK');
const LOAD_NL_SUBMIT_REPORT_FAIL = namespaced('LOAD_NL_SUBMIT_REPORT_FAIL');
const LOAD_VAT_BOOK_STYLESHEET_DATA = namespaced('LOAD_VAT_BOOK_STYLESHEET_DATA');
const LOAD_VAT_BOOK_STYLESHEET_DATA_OK = namespaced('LOAD_VAT_BOOK_STYLESHEET_DATA_OK');
const LOAD_VAT_BOOK_STYLESHEET_DATA_FAIL = namespaced('LOAD_VAT_BOOK_STYLESHEET_DATA_FAIL');
const GET_RETURNS = namespaced('GET_RETURNS');
const GET_RETURNS_OK = namespaced('GET_RETURNS_OK');
const GET_RETURNS_FAIL = namespaced('GET_RETURNS_FAIL');
const GET_RETURNS_DOCUMENT = namespaced('GET_RETURNS_DOCUMENT');
const GET_RETURNS_DOCUMENT_OK = namespaced('GET_RETURNS_DOCUMENT_OK');
const GET_RETURNS_DOCUMENT_FAIL = namespaced('GET_RETURNS_DOCUMENT_FAIL');
const GET_RETURN_RAW_FORM_XML = namespaced('GET_RETURN_RAW_FORM_XML');
const GET_RETURN_RAW_FORM_XML_OK = namespaced('GET_RETURN_RAW_FORM_XML_OK');
const GET_RETURN_RAW_FORM_XML_FAIL = namespaced('GET_RETURN_RAW_FORM_XML_FAIL');
const SAVE_TO_WORKFLOW = namespaced('SAVE_TO_WORKFLOW');
const SAVE_TO_WORKFLOW_OK = namespaced('SAVE_TO_WORKFLOW_OK');
const SAVE_TO_WORKFLOW_FAIL = namespaced('SAVE_TO_WORKFLOW_FAIL');
const GET_CAN_USER_SAVE_TO_WORKFLOW_OK = namespaced('GET_CAN_USER_SAVE_TO_WORKFLOW_OK');
const GET_CAN_USER_SAVE_TO_WORKFLOW_FAIL = namespaced('GET_CAN_USER_SAVE_TO_WORKFLOW_FAIL');
const CLOSE_VAT_BOOK = namespaced('CLOSE_VAT_BOOK');
const CLOSE_VAT_BOOK_OK = namespaced('CLOSE_VAT_BOOK_OK');
const CLOSE_VAT_BOOK_FAIL = namespaced('CLOSE_VAT_BOOK_FAIL');
const CLOSE_RETURN = namespaced('CLOSE_RETURN');
const CLOSE_RETURN_OK = namespaced('CLOSE_RETURN_OK');
const CLOSE_RETURN_FAIL = namespaced('CLOSE_RETURN_FAIL');
const LOAD_INTRASTAT_COMMERCIAL_SUMMARY = namespaced('LOAD_INTRASTAT_COMMERCIAL_SUMMARY');
const LOAD_INTRASTAT_COMMERCIAL_SUMMARY_OK = namespaced('LOAD_INTRASTAT_COMMERCIAL_SUMMARY_OK');
const LOAD_INTRASTAT_COMMERCIAL_SUMMARY_FAIL = namespaced('LOAD_INTRASTAT_COMMERCIAL_SUMMARY_FAIL');
const INITIATE_AUTO_SUBMISSION = namespaced('INITIATE_AUTO_SUBMISSION');
const INITIATE_AUTO_SUBMISSION_OK = namespaced('INITIATE_AUTO_SUBMISSION_OK');
const INITIATE_AUTO_SUBMISSION_FAIL = namespaced('INITIATE_AUTO_SUBMISSION_FAIL');
const CHECK_WORKFLOW_IN_PROGRESS_OK = namespaced('CHECK_WORKFLOW_IN_PROGRESS_OK');
const CHECK_WORKFLOW_IN_PROGRESS_FAIL = namespaced('CHECK_WORKFLOW_IN_PROGRESS_FAIL');
const LOAD_BOX_DESCRIPTIONS = namespaced('LOAD_BOX_DESCRIPTIONS');
const LOAD_BOX_DESCRIPTIONS_OK = namespaced('LOAD_BOX_DESCRIPTIONS_OK');
const LOAD_BOX_DESCRIPTIONS_FAIL = namespaced('LOAD_BOX_DESCRIPTIONS_FAIL');
const UPDATE_BOX_VALUE_AUTO_CORRECT_FLAG = namespaced('UPDATE_BOX_VALUE_AUTO_CORRECT_FLAG');
const UPDATE_BOX_VALUE_AUTO_CORRECT_FLAG_OK = namespaced('UPDATE_BOX_VALUE_AUTO_CORRECT_FLAG_OK');
const UPDATE_BOX_VALUE_AUTO_CORRECT_FLAG_FAIL = namespaced('UPDATE_BOX_VALUE_AUTO_CORRECT_FLAG_FAIL');

const DECountryCode = "DE";
const SECountryCode = "SE";
const ITCountryCode = "IT";
const EECountryCode = "EE";
const HUCountryCode = "HU";

const EXPORT_TRANSACTIONS = namespaced('EXPORT_TRANSACTIONS');
const EXPORT_TRANSACTIONS_OK =  namespaced('EXPORT_TRANSACTIONS_OK');
const EXPORT_TRANSACTIONS_FAIL =  namespaced('EXPORT_TRANSACTIONS_FAIL');

export const ACTION_TYPES = {
  UNLOAD_VALUES,
  LOAD_VALUES,
  LOAD_VALUES_OK,
  LOAD_VALUES_FAIL,
  LOAD_VALUE_DETAILS,
  LOAD_VALUE_DETAILS_OK,
  LOAD_VALUE_DETAILS_FAIL,
  EXPORT_VALUE_DETAILS,
  EXPORT_VALUE_DETAILS_OK,
  EXPORT_VALUE_DETAILS_FAIL,
  CREATE_FILING_DOCUMENT: CREATE_FILING_DOCUMENT,
  CREATE_FILING_DOCUMENT_OK: CREATE_FILING_DOCUMENT_OK,
  CREATE_FILING_DOCUMENT_FAIL: CREATE_FILING_DOCUMENT_FAIL,
  CLEAR_FILING_DOCUMENT_DETAILS,
  LOAD_EC_LISTING_DETAILS_OK,
  LOAD_EC_LISTING_DETAILS_FAIL,
  CLEAR_VALUE_DETAILS,
  LOAD_INTRA_DETAILS,
  LOAD_INTRA_DETAILS_OK,
  LOAD_INTRA_DETAILS_FAIL,
  DOWNLOAD_FILING_DOCUMENT_OK,
  DOWNLOAD_FILING_DOCUMENT_FAIL,
  CHECK_VATNUMBER,
  CHECK_VATNUMBER_OK,
  CHECK_VATNUMBER_FAIL,
  CHECK_VATNUMBER_INPROGRESS,
  CHECK_VATNUMBER_DONE,
  RELIABILITY_CHECK_VATNUMBER,
  RELIABILITY_CHECK_VATNUMBER_OK,
  RELIABILITY_CHECK_VATNUMBER_FAIL,
  LOAD_DE_SUBMIT_REPORT,
  LOAD_DE_SUBMIT_REPORT_OK,
  LOAD_DE_SUBMIT_REPORT_FAIL,
  LOAD_NL_SUBMIT_REPORT,
  LOAD_NL_SUBMIT_REPORT_OK,
  LOAD_NL_SUBMIT_REPORT_FAIL,
  LOAD_VAT_BOOK_STYLESHEET_DATA,
  LOAD_VAT_BOOK_STYLESHEET_DATA_OK,
  LOAD_VAT_BOOK_STYLESHEET_DATA_FAIL,
  GET_RETURNS,
  GET_RETURNS_OK,
  GET_RETURNS_FAIL,
  GET_RETURNS_DOCUMENT,
  GET_RETURNS_DOCUMENT_OK,
  GET_RETURNS_DOCUMENT_FAIL,
  GET_RETURN_RAW_FORM_XML,
  GET_RETURN_RAW_FORM_XML_OK,
  GET_RETURN_RAW_FORM_XML_FAIL,
  SAVE_TO_WORKFLOW,
  SAVE_TO_WORKFLOW_OK,
  SAVE_TO_WORKFLOW_FAIL,
  GET_CAN_USER_SAVE_TO_WORKFLOW_OK,
  GET_CAN_USER_SAVE_TO_WORKFLOW_FAIL,
  EXPORT_TRANSACTIONS,
  EXPORT_TRANSACTIONS_OK,
  EXPORT_TRANSACTIONS_FAIL,
  CLOSE_VAT_BOOK,
  CLOSE_VAT_BOOK_OK,
  CLOSE_VAT_BOOK_FAIL,
  CLOSE_RETURN,
  CLOSE_RETURN_OK,
  CLOSE_RETURN_FAIL,
  LOAD_INTRASTAT_COMMERCIAL_SUMMARY,
  LOAD_INTRASTAT_COMMERCIAL_SUMMARY_OK,
  LOAD_INTRASTAT_COMMERCIAL_SUMMARY_FAIL,
  INITIATE_AUTO_SUBMISSION,
  INITIATE_AUTO_SUBMISSION_OK,
  INITIATE_AUTO_SUBMISSION_FAIL,
  CHECK_WORKFLOW_IN_PROGRESS_OK,
  CHECK_WORKFLOW_IN_PROGRESS_FAIL,
  LOAD_BOX_DESCRIPTIONS,
  LOAD_BOX_DESCRIPTIONS_OK,
  LOAD_BOX_DESCRIPTIONS_FAIL,
  UPDATE_BOX_VALUE_AUTO_CORRECT_FLAG,
  UPDATE_BOX_VALUE_AUTO_CORRECT_FLAG_OK,
  UPDATE_BOX_VALUE_AUTO_CORRECT_FLAG_FAIL,
};

// In redux parlance, this is a reducer
export const updateSubStateFromAction = (state = { data: undefined, loading: false, filingValuesLoading: false, error: null, uploading: false, checkedVATNumbers: [], checkVATNumbersInProgress: [], workflowInProgress: true }, action) => {
  log.debug('updateSubStateFromAction: ', action, state);
  const getNewState = (action, state) => {
    switch (action.type) {
      case LOAD_VALUES:
        return Object.assign({}, state, { data: undefined, checkedVATNumbers: [], filingDocumentDetails: undefined, filingValueDetails: undefined, reliabilityCheckVATNumbers: undefined, filingValuesLoading: true, error: null });
      case UNLOAD_VALUES:
        return Object.assign({}, state, { data: undefined, checkedVATNumbers: [], filingDocumentDetails: undefined, filingValueDetails: undefined, reliabilityCheckVATNumbers: undefined, filingValuesLoading: false, error: null });
      case LOAD_VALUES_OK:
        return Object.assign({}, state, { data: action.response, filingValuesLoading: false, error: null });
      case LOAD_VALUES_FAIL:
        return Object.assign({}, state, { data: undefined, filingValuesLoading: false, error: action.error });
      case LOAD_VALUE_DETAILS:
        return Object.assign({}, state, { loading: true, error: null }); //filingValueDetails: null,
      case LOAD_VALUE_DETAILS_OK:
        return Object.assign({}, state, { filingValueDetails: action.response, loading: false, error: null });
      case LOAD_VALUE_DETAILS_FAIL:
        return Object.assign({}, state, { filingValueDetails: undefined, loading: false, error: action.error });
      case EXPORT_VALUE_DETAILS:
        return Object.assign({}, state, { loading: true, error: null }); //filingValueDetails: null,
      case EXPORT_VALUE_DETAILS_OK:
        return Object.assign({}, state, { loading: false, error: null }); //filingValueDetails: action.response,
      case EXPORT_VALUE_DETAILS_FAIL:
        return Object.assign({}, state, { loading: false, error: action.error }); //filingValueDetails: undefined,
      case CREATE_FILING_DOCUMENT:
        return Object.assign({}, state, { loading: true, error: null });
      case CREATE_FILING_DOCUMENT_OK:
        return Object.assign({}, state, { filingDocumentDetails: action.response, loading: false, error: null });
      case CREATE_FILING_DOCUMENT_FAIL:
        return Object.assign({}, state, { filingDocumentDetails: undefined, loading: false, error: action.error });
      case CLEAR_FILING_DOCUMENT_DETAILS:
        return Object.assign({}, state, { filingDocumentDetails: undefined, loading: false, error: null });
      case DOWNLOAD_FILING_DOCUMENT_OK:
        return Object.assign({}, state, { filingDocumentDetails: action.response, loading: false, error: null });
      case DOWNLOAD_FILING_DOCUMENT_FAIL:
        return Object.assign({}, state, { filingDocumentDetails: undefined, loading: false, error: action.error });
      case LOAD_EC_LISTING_DETAILS_OK:
        return Object.assign({}, state, { ecListingDetails: action.response, loading: false, error: null });
      case LOAD_EC_LISTING_DETAILS_FAIL:
        return Object.assign({}, state, { ecListingDetails: undefined, loading: false, error: action.error });
      case LOAD_INTRA_DETAILS:
        return Object.assign({}, state, { intrastatDetails: null, loading: true, error: null });
      case LOAD_INTRA_DETAILS_OK:
        return Object.assign({}, state, { intrastatDetails: action.response, loading: false, error: null });
      case LOAD_INTRA_DETAILS_FAIL:
        return Object.assign({}, state, { intrastatDetails: undefined, loading: false, error: action.error });
      case CLEAR_VALUE_DETAILS:
        return Object.assign({}, state, { filingValueDetails: undefined, loading: false, error: null });
      case CHECK_VATNUMBER:
        return Object.assign({}, state, { checkedVATNumbers: undefined, loading: true });
      case CHECK_VATNUMBER_OK:
        // Remove an existing value from state if this number is being checked again
        let filteredCheckedVATNumbers = state.checkedVATNumbers.filter((item) => { return item.vrn !== action.response.vrn });
        return Object.assign({}, state, { checkedVATNumbers: filteredCheckedVATNumbers.concat(action.response), loading: false });
      case CHECK_VATNUMBER_FAIL:
        return Object.assign({}, state, { checkedVATNumbers: undefined, loading: false, error: action.error });
      case CHECK_VATNUMBER_INPROGRESS:
        return Object.assign({}, state, { checkVATNumbersInProgress: state.checkVATNumbersInProgress.concat(action.vrn.vatNumber) });
      case CHECK_VATNUMBER_DONE:
        let updatedInProgressList = state.checkVATNumbersInProgress.filter((item) => { return item !== action.vrn.vatNumber });
        return Object.assign({}, state, { checkVATNumbersInProgress: updatedInProgressList });
      case RELIABILITY_CHECK_VATNUMBER:
        return Object.assign({}, state, { reliabilityCheckVATNumbers: undefined, loading: true });
      case RELIABILITY_CHECK_VATNUMBER_OK:
        return Object.assign({}, state, { reliabilityCheckVATNumbers: action.response, loading: false });
      case RELIABILITY_CHECK_VATNUMBER_FAIL:
        return Object.assign({}, state, { reliabilityCheckVATNumbers: undefined, loading: false });
      case LOAD_DE_SUBMIT_REPORT:
        return Object.assign({}, state, { loading: true, error: null });
      case LOAD_DE_SUBMIT_REPORT_OK:
        return Object.assign({}, state, { loading: false, error: null });
      case LOAD_DE_SUBMIT_REPORT_FAIL:
        return Object.assign({}, state, { loading: false, error: action.error });
      case LOAD_NL_SUBMIT_REPORT:
        return Object.assign({}, state, { loading: true, error: null });
      case LOAD_NL_SUBMIT_REPORT_OK:
        return Object.assign({}, state, { loading: false, error: null });
      case LOAD_NL_SUBMIT_REPORT_FAIL:
        return Object.assign({}, state, { loading: false, error: action.error });
      case LOAD_VAT_BOOK_STYLESHEET_DATA:
        return Object.assign({}, state, { loading: true, vatBookStyleSheetData: null, error: null });
      case LOAD_VAT_BOOK_STYLESHEET_DATA_OK:
        return Object.assign({}, state, { loading: false, vatBookStyleSheetData: action.response, error: null });
      case LOAD_VAT_BOOK_STYLESHEET_DATA_FAIL:
        return Object.assign({}, state, { loading: false, vatBookStyleSheetData: null, error: action.error });
      case GET_RETURNS:
        return Object.assign({}, state, { returnsData: undefined, loading: true, error: null });
      case GET_RETURNS_OK:
        return Object.assign({}, state, { returnsData: action.response, loading: false, error: null });
      case GET_RETURNS_FAIL:
        return Object.assign({}, state, { returnsData: undefined, loading: false, error: action.error });
      case GET_RETURNS_DOCUMENT:
        return Object.assign({}, state, { returnsDocumentAsBlob: undefined, loading: true, error: null });
      case GET_RETURNS_DOCUMENT_OK:
        return Object.assign({}, state, { returnsDocumentAsBlob: action.response, loading: false, error: null });
      case GET_RETURNS_DOCUMENT_FAIL:
        return Object.assign({}, state, { returnsDocumentAsBlob: undefined, loading: false, error: action.error });
      case SAVE_TO_WORKFLOW_OK:
        return Object.assign({}, state, { loading: false, error: null });
      case SAVE_TO_WORKFLOW_FAIL:
        return Object.assign({}, state, { loading: false, error: action.error });
      case GET_CAN_USER_SAVE_TO_WORKFLOW_OK:
        return Object.assign({}, state, { canUserSaveToWorkflow: action.response, loading: false, error: null });
      case GET_CAN_USER_SAVE_TO_WORKFLOW_FAIL:
        return Object.assign({}, state, { canUserSaveToWorkflow: false, loading: false, error: action.error });
      case EXPORT_TRANSACTIONS:
        return Object.assign({}, state, { showToast: true, error: null });
      case EXPORT_TRANSACTIONS_OK:
        return Object.assign({}, state, { showToast: false, error: null });
      case EXPORT_TRANSACTIONS_FAIL:
        return Object.assign({}, state, { showToast: false, error: action.error });
      case CLOSE_VAT_BOOK:
        return { ...state, loading: true, error: null };
      case CLOSE_VAT_BOOK_OK:
        return { ...state, loading: false, error: null };
      case CLOSE_VAT_BOOK_FAIL:
        return { ...state, loading: false, error: action.error };
      case CLOSE_RETURN:
        return { ...state, loading: true, error: null };
      case CLOSE_RETURN_OK:
        return { ...state, loading: false, error: null };
      case CLOSE_RETURN_FAIL:
        return { ...state, loading: false, error: action.error };
      case LOAD_INTRASTAT_COMMERCIAL_SUMMARY:
        return { ...state, loadingIntrastatCommercialSummary: true, error: null };
      case LOAD_INTRASTAT_COMMERCIAL_SUMMARY_OK:
        return { ...state, intrastatCommercialSummary: action.response, loadingIntrastatCommercialSummary: false, error: null };
      case LOAD_INTRASTAT_COMMERCIAL_SUMMARY_FAIL:
        return { ...state, intrastatCommercialSummary: undefined, loadingIntrastatCommercialSummary: false, error: action.error };
      case INITIATE_AUTO_SUBMISSION:
        return { ...state, loading: true, error: null };
      case INITIATE_AUTO_SUBMISSION_OK:
        return { ...state, autoSubmissionResult: action.response, loading: false, error: null };
      case INITIATE_AUTO_SUBMISSION_FAIL:
        return { ...state, autoSubmissionResult: undefined, loading: false, error: action.error };
      case CHECK_WORKFLOW_IN_PROGRESS_OK:
        return { ...state, workflowInProgress: action.response, error: null };
      case CHECK_WORKFLOW_IN_PROGRESS_FAIL:
        // Fail closed: if we can't confirm it's safe, treat as in-progress so Initiate stays disabled.
        return { ...state, workflowInProgress: true, error: action.error };
      case LOAD_BOX_DESCRIPTIONS:
        return { ...state, boxDescriptions: undefined, boxDescriptionsError: null };
      case LOAD_BOX_DESCRIPTIONS_OK:
        return { ...state, boxDescriptions: action.response, boxDescriptionsError: null };
      case LOAD_BOX_DESCRIPTIONS_FAIL:
        return { ...state, boxDescriptions: undefined, boxDescriptionsError: action.error };
      case UPDATE_BOX_VALUE_AUTO_CORRECT_FLAG:
        return { ...state, updateBoxValueAutoCorrectFlagInProgress: true, updateBoxValueAutoCorrectFlagError: null };
      case UPDATE_BOX_VALUE_AUTO_CORRECT_FLAG_OK:
        return { ...state, updateBoxValueAutoCorrectFlagInProgress: false, updateBoxValueAutoCorrectFlagError: null };
      case UPDATE_BOX_VALUE_AUTO_CORRECT_FLAG_FAIL:
        return { ...state, updateBoxValueAutoCorrectFlagInProgress: false, updateBoxValueAutoCorrectFlagError: action.error };
      default:
        return state;
    }
  };

  const newState = getNewState(action, state);
  log.debug('updateSubStateFromAction:  newState', newState);

  return newState;
};

export const API = {
  checkVATNumber: (vatNumber) => {
    return (dispatch) => {
      log.clearDictionaryError(ALL_MESSAGES.ECL_VATNUMBER_CHECK_ERROR);
      log.debug('checkVATNumber(): vatNumber: ', vatNumber);
      dispatch({ type: CHECK_VATNUMBER_INPROGRESS, vrn: { vatNumber } });
      return CLIENT.getOnlineVATNumberCheck(vatNumber)
        .then((response) => {
          // Prepend the VAT Number into the response object because it is needed later
          let returnJson = { "vrn": vatNumber };
          Object.assign(returnJson, response);
          dispatch({ type: CHECK_VATNUMBER_OK, response: returnJson });
        })
        .catch((error) => {
          log.setDictionaryMessage(ALL_MESSAGES.ECL_VATNUMBER_CHECK_ERROR);
          dispatch({ type: CHECK_VATNUMBER_FAIL, error });
        })
        .then(() => {
          dispatch({ type: CHECK_VATNUMBER_DONE, vrn: { vatNumber } });
        });
    };
  },

  checkVATNumberWithRequestId: (vatNumber, requesterVATNumber) => {
    return (dispatch) => {
      log.clearDictionaryError(ALL_MESSAGES.ECL_VATNUMBER_CHECK_ERROR);
      log.debug('checkVATNumber(): vatNumber: requesterVATNumber ', vatNumber, requesterVATNumber);
      dispatch({ type: CHECK_VATNUMBER_INPROGRESS, vrn: { vatNumber } });
      return CLIENT.getOnlineVATNumberCheckWithRequestId(vatNumber, requesterVATNumber)
        .then((response) => {
          // Prepend the VAT Number into the response object because it is needed later
          let returnJson = { "vrn": vatNumber };
          Object.assign(returnJson, response);
          dispatch({ type: CHECK_VATNUMBER_OK, response: returnJson });
        })
        .catch((error) => {
          //Show message ECL_VATNUMBER_CHECK_ERROR
          log.setDictionaryMessage(ALL_MESSAGES.ECL_VATNUMBER_CHECK_ERROR);
          dispatch({ type: CHECK_VATNUMBER_FAIL, error });
        })
        .then(() => {
          dispatch({ type: CHECK_VATNUMBER_DONE, vrn: { vatNumber } });
        });
    };
  },

  reliabilityCheckVATNumber: (vatNumbers) => {
    return (dispatch) => {
      log.clearDictionaryError(ALL_MESSAGES.ECL_VATNUMBER_CHECK_ERROR);
      log.debug('reliabilityCheckVATNumber(): vatNumber: ', vatNumbers);
      dispatch({ type: RELIABILITY_CHECK_VATNUMBER });
      return CLIENT.getReliabilityCheckVATNumberCheck(vatNumbers)
        .then((response) => {
          log.debug('reliabilityCheckVATNumber(): response: ', response);
          dispatch({ type: RELIABILITY_CHECK_VATNUMBER_OK, response: response });
        })
        .catch((error) => {
          log.debug('reliabilityCheckVATNumber(): error: ', error);
            log.setDictionaryMessage(ALL_MESSAGES.ECL_VATNUMBER_CHECK_ERROR);
          dispatch({ type: RELIABILITY_CHECK_VATNUMBER_FAIL, error });
        });
    };
  },

  doLoadValues: (filterParams) => {
    return (dispatch, getState) => {
      dispatch({ type: LOAD_VALUES });
      log.debug('doLoadValues(): filterParams: ', filterParams);
      return CLIENT.getFilingValues(filterParams)
        .then((response) => {
          dispatch({ type: LOAD_VALUES_OK, response: response });
        })
        .catch((error) => {
          dispatch({ type: LOAD_VALUES_FAIL, error });
        });
    };
  },

  doLoadBoxDescriptions: (filterParams) => {
    return (dispatch) => {
      dispatch({ type: LOAD_BOX_DESCRIPTIONS });
      log.debug('doLoadBoxDescriptions(): filterParams: ', filterParams);
      return CLIENT.getBoxDescriptions(filterParams)
        .then((response) => {
          dispatch({ type: LOAD_BOX_DESCRIPTIONS_OK, response: response });
        })
        .catch((error) => {
          log.debug('doLoadBoxDescriptions(): error: ', error);
          dispatch({ type: LOAD_BOX_DESCRIPTIONS_FAIL, error });
        });
    };
  },

  doLoadGroupedCorrections: (filterParams) => {
    return async (dispatch) => {
      dispatch({ type: LOAD_VALUES });
      try {
        const response = await CLIENT.getGroupedCorrections(filterParams);
        dispatch({ type: LOAD_VALUES_OK, response: response });
      } catch (error) {
        dispatch({ type: LOAD_VALUES_FAIL, error });
      }
    };
  },

  doLoadECGroupedCorrections: (filterParams) => {
    return async (dispatch) => {
      dispatch({ type: LOAD_VALUES });
      try {
        const response = await CLIENT.getECGroupedCorrections(filterParams);
        dispatch({ type: LOAD_VALUES_OK, response: response });
      } catch (error) {
        dispatch({ type: LOAD_VALUES_FAIL, error });
      }
    };
  },

  resetFilingValues: () => {
    return (dispatch, getState) => {
      log.debug('UNLOAD_VALUES: ');
      dispatch({ type: UNLOAD_VALUES });
    };
  },

  doLoadLocalListingFilingValues: (filterParams) => {
    return (dispatch, getState) => {
      dispatch({ type: LOAD_VALUES });
      log.debug('doLoadLocalListingFilingValues(): filterParams: ', filterParams);
      return CLIENT.getLocalListingFilingValues(filterParams)
        .then((response) => {
          dispatch({ type: LOAD_VALUES_OK, response: response });
        })
        .catch((error) => {
          dispatch({ type: LOAD_VALUES_FAIL, error });
        });
    };
  },

  doLoadEcListingReports: (params) => {
    return (dispatch, getState) => {
      log.clearDictionaryError(ALL_MESSAGES.ECL_VATNUMBER_CHECK_ERROR);
      dispatch({ type: LOAD_VALUES });
      log.debug('doLoadEcListingReports(): params: ', params);
      return CLIENT.getEcListingReports(params)
        .then((response) => {
          dispatch({ type: LOAD_VALUES_OK, response: response });
        })
        .catch((error) => {
          dispatch({ type: LOAD_VALUES_FAIL, error });
        });
    };
  },

  doLoadIntrastatReports: (params) => {
    return (dispatch, getState) => {
      dispatch({ type: LOAD_VALUES });
      log.debug('doLoadIntrastatReports(): params: ', params);
      return CLIENT.getIntrastatReports(params)
        .then((response) => {
          dispatch({ type: LOAD_VALUES_OK, response: formatResponseForIntrastatReport(response) }); //format InvoiceNrs and LineNrs
        })
        .catch((error) => {
          dispatch({ type: LOAD_VALUES_FAIL, error });
        });
    };
  },

  doLoadExtrastatReports: (params) => {
    return (dispatch, getState) => {
      dispatch({ type: LOAD_VALUES });
      log.debug('doLoadExtrastatReports(): params: ', params);
      return CLIENT.getExtrastatReports(params)
        .then((response) => {
          dispatch({ type: LOAD_VALUES_OK, response: response });
        })
        .catch((error) => {
          dispatch({ type: LOAD_VALUES_FAIL, error });
        });
    };
  },

  doLoadReverseChargeListingValues: (params) => {
    return (dispatch, getState) => {
      dispatch({ type: LOAD_VALUES });
      log.debug('doLoadReverseChargeListingValues(): params: ', params);
      return CLIENT.getReverseChargeListingFilingValues(params)
        .then((response) => {
          dispatch({ type: LOAD_VALUES_OK, response: response });
        })
        .catch((error) => {
          dispatch({ type: LOAD_VALUES_FAIL, error });
        });
    };
  },

  doLoadReverseChargeListingValueDetails: (params) => {
    return (dispatch, getState) => {
      dispatch({ type: LOAD_VALUE_DETAILS });
      log.debug('doLoadReverseChargeListingValueDetails(): params: ', params);
      return CLIENT.getReverseChargeListingValueDetails(params)
        .then((response) => {
          dispatch({ type: LOAD_VALUE_DETAILS_OK, response: response });
        })
        .catch((error) => {
          dispatch({ type: LOAD_VALUE_DETAILS_FAIL, error });
        });
    };
  },

  doLoadLocalListingValueDetails: (params) => {
    return (dispatch, getState) => {
      dispatch({ type: LOAD_VALUE_DETAILS });
      log.debug('doLoadLocalListingValueDetails(): params: ', params);
      return CLIENT.getLocalListingValueDetails(params)
        .then((response) => {
          dispatch({ type: LOAD_VALUE_DETAILS_OK, response: response });
        })
        .catch((error) => {
          dispatch({ type: LOAD_VALUE_DETAILS_FAIL, error });
        });
    };
  },

  doLoadValueDetails: (filterParams) => {
    return (dispatch, getState) => {
      dispatch({ type: LOAD_VALUE_DETAILS });
      log.debug('doLoadValueDetails(): filterParams: ', filterParams);
      return CLIENT.getFilingValueDetails(filterParams)
        .then((response) => {
          dispatch({ type: LOAD_VALUE_DETAILS_OK, response: response });
        })
        .catch((error) => {
          dispatch({ type: LOAD_VALUE_DETAILS_FAIL, error });
        });
    };
  },

  doExportVatBoxDetailsCSV: (filterParams, callback) => {
    return (dispatch, getState) => {
      dispatch({ type: EXPORT_VALUE_DETAILS });
      log.debug('doExport():');

      return CLIENT.doExportVatBoxDetailsCSV(filterParams)
        .then((response) => {
          log.debug('doExport(): succeeded: ', response);
          dispatch({ type: EXPORT_VALUE_DETAILS_OK, response: response });

          if (typeof callback === 'function') {
            callback(null, response);
          }
        })
        .catch((error) => {
          log.debug('doExport(): failed: ', error);
          dispatch({ type: EXPORT_VALUE_DETAILS_FAIL, error });

          if (typeof callback === 'function') {
            callback(error, null);
          }
        });
    };
  },

  doHideValueDetails: () => {
    return (dispatch, getState) => {
      dispatch({ type: CLEAR_VALUE_DETAILS });
    };
  },



  doCreateFilingDocument: (formXmlId, filingTypeId, body, downloadInBrowser = true, callback = () => { }) => {
    return (dispatch, getState) => {

      const { featureFlag } = getState();
      dispatch({ type: CREATE_FILING_DOCUMENT });
      log.debug('doCreateFilingDocument(): formXmlId: ', formXmlId);
      log.debug('doCreateFilingDocument(): filingTypeId: ', filingTypeId);
      log.debug('doCreateFilingDocument(): body: ', body);

      CLIENT.getFilingDocumentName(formXmlId, filingTypeId, body, featureFlag)
        .then((response) => {
          if (response.length < 1)
            dispatch({ type: CREATE_FILING_DOCUMENT_FAIL });

          var responseObj = response[0];
          // This request is for an approval so force a download or in case of vat books download xml
          if (body.reportApproval.approvalDate || filingTypeId === FilingTypeEnum.VATBOOKS || response.length > 1) {
            log.debug('Approved: response.uri', response);

            // We have the outputType to go on, corelate these to file extension and mimeType
            // https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Complete_list_of_MIME_types
            // there will be more to add as we add all the formats you can save in VAT Reporting
            let ext, mimeType;
            switch (body.outputType) {
              case 'PDF':
                ext = responseObj.fileExtension || 'pdf';
                mimeType = 'application/pdf';
                break;
              case 'XML':
                ext = responseObj.fileExtension || 'xml';
                mimeType = 'application/xml';
                break;
              case 'FixedFormat':
                ext = responseObj.fileExtension || 'txt';
                mimeType = 'text/plain';
                break;
              default:
                ext = responseObj.fileExtension || 'txt';
                mimeType = 'text/plain';
            }
            var fileblob;
            response.forEach((responseObj, counter) => {
              // Set a timeout on the request to stagger the calls by msInterval
              setTimeout(() => {
                // Transform the uri into a blob and use FileSaver to download it client side
                // better ways of downloading like content-disposition didn't work with fetch
                // so this was the least bad implementation option
                CLIENT.getFilingDocumentAsBlob(mimeType, responseObj.uri)
                  .then((blob) => {
                    // Create a filename like: "VAT return CZ (2018-06-01_2018-06-30) 2018-7-31.pdf"
                    fileblob = blob;
                    let filename = null;
                    let useSystemFileName = false;
                    if (responseObj.filingDocumentFileName) {
                      if (
                        ((body.countryCode === DECountryCode || body.countryCode === SECountryCode) &&
                          body.filingName === ENUMS.getFilingNameFromFilingType(FilingTypeEnum.INTRASTAT) &&
                          body.outputType.toUpperCase() === FilingFileExtention.XML.toUpperCase()) ||

                        (body.countryCode === ITCountryCode && ["VAT Return", "Intrastat"].includes(body.filingName)) ||

                        (body.countryCode === DECountryCode &&
                          body.filingName === ENUMS.getFilingNameFromFilingType(FilingTypeEnum.ECLISTING) &&
                          body.outputType.toUpperCase() === FilingFileExtention.FIXEDFORMAT.toUpperCase() &&
                          ext.toUpperCase() !== FilingFileExtention.CSV.toUpperCase()) ||

                        (body.countryCode === HUCountryCode &&
                          (body.filingName === ENUMS.getFilingNameFromFilingType(FilingTypeEnum.VATRETURN) ||
                          body.filingName === ENUMS.getFilingNameFromFilingType(FilingTypeEnum.ECLISTING))
                        ) ||

                        FilingHelper.isFilingForCorrectionPeriod(body.returnDisplayType)
                      ) {
                        //Use filename form the API to match the VAT Desktop
                        useSystemFileName = true;
                      }
                    }

                    if (useSystemFileName) {
                      filename = `${responseObj.filingDocumentFileName}.${ext}`;
                    } else if (response.length > 1) {
                      filename = responseObj.filingDocumentFileName;
                      fileblob = '';
                    }
                    else {
                      let filingName = body.countryCode === EECountryCode ? "VAT" : body.filingName;
                      filename = `${filingName} ${body.countryCode} (${body.filingPeriod.fromDate}_${body.filingPeriod.toDate}) ${body.reportApproval.approvalDate}.${ext}`;
                    }
                    if (downloadInBrowser) {
                      // Download in the browser
                      FileSaver.saveAs(blob, filename);
                      if (FilingHelper.isRawformXmlSupported(filingTypeId, body.countryCode)) {
                        CLIENT.getFilingDocumentAsBlob('application/xml', `${responseObj.uri}.rawformxml`)
                          .then((rawformBlob) => {
                            return FilingHelper.updateRawformXmlOutputFileValue(rawformBlob, filename);
                          })
                          .then((updatedRawformBlob) => {
                            FileSaver.saveAs(updatedRawformBlob, `${filename}.rawformxml`);
                          })
                          .catch((error) => {
                            log.error('doCreateFilingDocument: error in saving the rawform xml file', error);
                          });
                      }
                    }
                    log.debug('doCreateFilingDocument', filingTypeId, formXmlId);
                    callback(null, {
                      ext: ext,
                      mimeType: mimeType,
                      filename: filename,
                      content: blob
                    }, null);
                  }
                  )
                  .catch((error) => {
                    dispatch({ type: DOWNLOAD_FILING_DOCUMENT_FAIL, error });
                    callback(error, null);
                  });
              }, counter * DelayTimeInterval);
            });
            dispatch({ type: DOWNLOAD_FILING_DOCUMENT_OK, reponse: fileblob });
          }
          else
          {
            //For VAT return on view filing show warning message
            let warningMessage = [];
            let manualCorrectionBoxes = [];
            let isBoxValueAutoCorrected = false;
            if (filingTypeId === FilingTypeEnum.VATRETURN) {
              warningMessage = responseObj.warningMessage;
              manualCorrectionBoxes = responseObj.manualCorrectionBoxes;
              isBoxValueAutoCorrected = responseObj.isBoxValueAutoCorrected;
              log.debug('doCreateFilingDocument warningMessage', warningMessage);

              callback(null, {}, warningMessage, manualCorrectionBoxes, isBoxValueAutoCorrected);
            }
          }
          dispatch({
            type: CREATE_FILING_DOCUMENT_OK, response: {
              uri: (response.length > 1 || body.reportApproval.approvalDate) ? '' : responseObj.uri,
              ext: responseObj.fileExtension,
              filingDocumentFileName: responseObj.filingDocumentFileName,
              multipleFilesToDownload: (response.length > 1 && !body.reportApproval.approvalDate)
            }
          });
        })
        .catch((error) => {
          dispatch({ type: CREATE_FILING_DOCUMENT_FAIL, error });
          callback(error, null);
        });
    }
  },

  clearFilingDocumentDetails: () => {
    return (dispatch, getState) => {
      log.debug('clearFilingDocumentDetails()');
      dispatch({ type: CLEAR_FILING_DOCUMENT_DETAILS });
    };
  },

  doLoadEcListingDetails: (params) => {
    return (dispatch, getState) => {
      //dispatch({type: LOAD_VALUES});
      log.debug('doLoadEcListingDetails(): params: ', params);
      return CLIENT.getEcListingDetails(params)
        .then((response) => {
          dispatch({ type: LOAD_EC_LISTING_DETAILS_OK, response: response });
        })
        .catch((error) => {
          dispatch({ type: LOAD_EC_LISTING_DETAILS_FAIL, error });
        });
    };
  },
  doLoadEcListingLineDetails: (params) => {
    return (dispatch) => {
      log.debug('doLoadEcListingLineDetails(): params: ', params);
      return CLIENT.getEcListingLineDetails(params)
        .then((response) => {
          dispatch({ type: LOAD_EC_LISTING_DETAILS_OK, response: response });
        })
        .catch((error) => {
          dispatch({ type: LOAD_EC_LISTING_DETAILS_FAIL, error });
        });
    };
  },
  doLoadIntrastatDetails: (lineNumbers, pagination, callback) => {
    let lineIDs = getLineIDsForCurrentPage(lineNumbers, pagination);
    return (dispatch, getState) => {
      dispatch({ type: LOAD_INTRA_DETAILS });
      log.debug('doLoadIntrastatDetails(): params: ', lineNumbers);
      return CLIENT.getIntrastatDetails(lineIDs)
        .then((response) => {
          dispatch({ type: LOAD_INTRA_DETAILS_OK, response: response });
          callback(response);
        })
        .catch((error) => {
          dispatch({ type: LOAD_INTRA_DETAILS_FAIL, error });
          callback(error);
        });
    };
  },

  doGetVATBooks: (params) => {
    return (dispatch) => {
      dispatch({ type: LOAD_VALUES });
      log.debug('doGetVATBooks(): params: ', params);
      return CLIENT.getVATBooks(params)
        .then((response) => {
          dispatch({ type: LOAD_VALUES_OK, response: response });
        })
        .catch((error) => {
          dispatch({ type: LOAD_VALUES_FAIL, error });
        });
    };
  },

  doSubmitDEReport: (certificateFile, reportUri, certificatePIN, reportType, filename, callback) => {
    return (dispatch, getState) => {
      dispatch({ type: LOAD_DE_SUBMIT_REPORT });
      const version = resolveSubmissionVersion(getState().featureFlag?.data, 'DE', reportType);
      log.debug('doSubmitDEReport(): version: %s, params: ', version, certificateFile, reportUri, certificatePIN);
      return CLIENT.SubmitDEReport(certificateFile, reportUri, certificatePIN, version)
        .then((response) => {
          callback(response, filename);
          dispatch({ type: LOAD_DE_SUBMIT_REPORT_OK, response: response });
        })
        .catch((error) => {
          dispatch({ type: LOAD_DE_SUBMIT_REPORT_FAIL, error });
        });
    };
  },

  doSubmitNLReport: (filingParamsNL, reportType, callback) => {
    return (dispatch, getState) => {
      dispatch({ type: LOAD_NL_SUBMIT_REPORT });
      const version = resolveSubmissionVersion(getState().featureFlag?.data, 'NL', reportType);
      log.debug('doSubmitNLReport(): version: %s, params: ', version, filingParamsNL);
      return CLIENT.SubmitNLReport(filingParamsNL, reportType, version)
        .then((response) => {
          callback(response);
          dispatch({ type: LOAD_NL_SUBMIT_REPORT_OK, response: response });
        })
        .catch((error) => {
          dispatch({ type: LOAD_NL_SUBMIT_REPORT_FAIL, error });
        });
    };
  },

  getFilingDocumentAsBlob: (type, uri, callback) => {
    return CLIENT.getFilingDocumentAsBlob(type, uri)
      .then((blob) => {
        log.debug('getFilingDocumentAsBlob got a response ', blob);
        callback(null, blob);
      })
      .catch((error) => {
        log.error('getFilingDocumentAsBlob got an error   ', error);
        callback(error, null);
      });
  },

  getFilingDocumentAsText: (type, uri, callback) => {
    return CLIENT.getFilingDocumentAsText(type, uri)
      .then((text) => {
        log.debug('getFilingDocumentAsBlob got a response ', text);
        callback(null, text);
      })
      .catch((error) => {
        log.error('getFilingDocumentAsBlob got an error   ', error);
        callback(error, null);
      });
    },

    getVatBookStyleSheetData: (documentId, errorHandler) => {
      let uri = vrRestApiHost + '/api/reports/download/' + documentId;
      return (dispatch) => {
          dispatch({ type: LOAD_VAT_BOOK_STYLESHEET_DATA });
          CLIENT.getFilingDocumentAsBlob('TEXT', uri)
              .then((blob) => {
                  log.debug('getVatBookStyleSheetData -> getFilingDocumentAsBlob got a response ', blob);
                  // Load the blob text with FileReader to specify the appropriate encoding.
                  // response.text() will decode with UTF-8 which causes some special
                  // characters to be lost
                  let reader = new FileReader();
                  reader.onload = () => {
                      dispatch({ type: LOAD_VAT_BOOK_STYLESHEET_DATA_OK, response: reader.result });
                  };

                  reader.onerror = () => {
                      let error = reader.error;
                      log.error(
                          'getVatBookStyleSheetData -> getFilingDocumentAsBlob encountered an error loading the blob ',
                          error
                      );
                      dispatch({ type: LOAD_VAT_BOOK_STYLESHEET_DATA_FAIL, error });
                  };

                  reader.readAsText(blob, 'ISO-8859-1');
              })
              .catch((error) => {
                  log.error('getVatBookStyleSheetData -> getFilingDocumentAsBlob encountered an error ', error);
                  dispatch({ type: LOAD_VAT_BOOK_STYLESHEET_DATA_FAIL, error });
                  errorHandler(error);
              });
      };
    },

    generateReturns: (returnDetails={}) => {
      log.debug('generateReturns(): ');
      return (dispatch) => {
        log.debug("generateReturns(): asyncActionCreator(): started");
        CLIENT.generateReturns(returnDetails).then( response => {
          log.debug("generateReturns(): asyncActionCreator(): ok");
        }).catch( error => {
          log.debug("generateReturns(): asyncActionCreator(): fail");
        });
      };
    },

    generateReturnsForIntrastat: (returnDetails={}, callback=()=>{}) => {
      log.debug('generateReturnsForIntrastat(): ');
      return (dispatch) => {
        log.debug("generateReturnsForIntrastat(): asyncActionCreator(): started");
        CLIENT.generateReturns(returnDetails).then( response => {
          log.debug("generateReturnsForIntrastat(): asyncActionCreator(): accepted");
          callback(null, response);
        }).catch( error => {
          log.debug("generateReturnsForIntrastat(): asyncActionCreator(): fail");
          callback(error, null);
        });
      };
    },

    generateReturnsForVatReturn: (returnDetails={}, callback=()=>{}) => {
      log.debug('generateReturnsForVatReturn(): ');
      return async (dispatch) => {
        log.debug("generateReturnsForVatReturn(): asyncActionCreator(): started");
        try {
          const response = await CLIENT.generateReturns(returnDetails);
          log.debug("generateReturnsForVatReturn(): asyncActionCreator(): accepted");
          callback(null, response);
        } catch (error) {
          log.debug("generateReturnsForVatReturn(): asyncActionCreator(): fail");
          callback(error, null);
        }
      };
    },

    getReturns: (params, callback=()=>{}) => {
      log.debug('getReturns(): ');
      return (dispatch) => {
        log.debug("getReturns(): asyncActionCreator(): started");
        dispatch({type: GET_RETURNS});
        CLIENT.getReturns(params).then( response => {
          log.debug("getReturns(): asyncActionCreator(): ok");
          dispatch({type: GET_RETURNS_OK, response: response});
          callback(null, response);
        }).catch( error => {
          log.debug("getReturns(): asyncActionCreator(): fail");
          dispatch({type: GET_RETURNS_FAIL, error: error});
          callback(error, null);
        });
      };
    },

    // Flips the IsBoxValueAutoCorrected flag on an existing return so the
    // backend kicks off the asynchronous box-value auto-correction job.
    // Triggered when the user clicks "Yes" on the box validation modal.
    doUpdateBoxValueAutoCorrectFlag: (params, callback=()=>{}) => {
      log.debug('doUpdateBoxValueAutoCorrectFlag(): params: ', params);
      return (dispatch) => {
        dispatch({type: UPDATE_BOX_VALUE_AUTO_CORRECT_FLAG});
        return CLIENT.updateBoxValueAutoCorrectFlag(params).then( response => {
          log.debug('doUpdateBoxValueAutoCorrectFlag(): ok');
          dispatch({type: UPDATE_BOX_VALUE_AUTO_CORRECT_FLAG_OK, response: response});
          callback(null, response);
        }).catch( error => {
          log.debug('doUpdateBoxValueAutoCorrectFlag(): fail', error);
          dispatch({type: UPDATE_BOX_VALUE_AUTO_CORRECT_FLAG_FAIL, error: error});
          callback(error, null);
        });
      };
    },

    getReturnsDocument: (documentType, url, callback=()=>{}) => {
      log.debug('getReturnsDocument(): ');
      return (dispatch) => {
        log.debug("getReturnsDocument(): asyncActionCreator(): started");
        dispatch({type: GET_RETURNS_DOCUMENT});
        CLIENT.getReturnsDocument(documentType, url).then( response => {
          log.debug("getReturnsDocument(): asyncActionCreator(): ok");
          dispatch({type: GET_RETURNS_DOCUMENT_OK, response: response});
          callback(null, response);
        }).catch( error => {
          log.debug("getReturnsDocument(): asyncActionCreator(): fail");
           dispatch({type: GET_RETURNS_DOCUMENT_FAIL, error: error});
           callback(error, null);
        });
      };
    },

    getReturnRawFormXml: (guid, callback = () => {}) => {
      log.debug('getReturnRawFormXml(): guid: ', guid);
      return async (dispatch) => {
        log.debug('getReturnRawFormXml(): asyncActionCreator(): started');
        dispatch({ type: GET_RETURN_RAW_FORM_XML });
        try {
          const response = await CLIENT.getRawFormXmlForReturn(guid);
          log.debug('getReturnRawFormXml(): asyncActionCreator(): ok');
          dispatch({ type: GET_RETURN_RAW_FORM_XML_OK, response });
          callback(null, response);
        } catch (error) {
          log.debug('getReturnRawFormXml(): asyncActionCreator(): fail');
          dispatch({ type: GET_RETURN_RAW_FORM_XML_FAIL, error });
          callback(error, null);
        }
      };
    },

    doLoadOSSReports: (params) => {
      return (dispatch, getState) => {
        dispatch({ type: LOAD_VALUES });
        log.debug('doLoadOSSReports(): params: ', params);
        return CLIENT.getOSSReports(params)
          .then((response) => {
            dispatch({
              type: LOAD_VALUES_OK,
              response: {
                ossData : response.ossUnion || response.ossNonUnion || response.ossImport
              }
            });
          })
          .catch((error) => {
            dispatch({ type: LOAD_VALUES_FAIL, error });
          });
      };
    },

    doLoadOSSReportsLatest: (params) => {
      return (dispatch) => {
        dispatch({ type: LOAD_VALUES });
        log.debug('doLoadOSSReportsLatest(): params: ', params);
        return CLIENT.getOSSReportsLatest(params)
          .then((response) => {
            dispatch({
              type: LOAD_VALUES_OK,
              response: {
                ossData: response.ossUnion || response.ossNonUnion || response.ossImport
              }
            });
          })
          .catch((error) => {
            dispatch({ type: LOAD_VALUES_FAIL, error });
          });
      };
    },

    doSaveToWorkflow: (inputParams, callback) => {
      const { isAsyncReturn, ...params } = FilingHelper.mapPayloadToWorkflowDto(inputParams);
      log.debug('doSaveToWorkflow(): ', params);
      return async (dispatch) => {
        log.debug('saveToWorkflow(): asyncActionCreator(): started');
        try {
          log.debug('saveToWorkflow(): asyncActionCreator(): sending primary file...');
          const response = await CLIENT.saveToWorkflow(params);
          log.debug('saveToWorkflow(): asyncActionCreator(): send primary file: ok');
          if (FilingHelper.isRawformXmlSupported(params.returnType, params.countryCode)) {
            log.debug('saveToWorkflow(): asyncActionCreator(): sending rawform xml file...');
            const rawformParams = { ...params };
            if (rawformParams.workflowSaveFileName != null) {
              rawformParams.workflowSaveFileName = `${rawformParams.workflowSaveFileName}.rawformxml`;
            }
            if (isAsyncReturn) {
              // Async flow: backend resolves the rawformxml S3 key from the IsAsyncRawFormXml flag.
              rawformParams.isAsyncRawFormXml = true;
            } else {
              // Sync flow: backend reads the rawformxml from Delphi using the suffixed file name.
              rawformParams.fileName = `${rawformParams.fileName}.rawformxml`;
            }
            await CLIENT.saveToWorkflow(rawformParams);
            log.debug('saveToWorkflow(): asyncActionCreator(): send rawform xml file: ok');
          }
          callback(null, response);
          dispatch({ type: SAVE_TO_WORKFLOW_OK, response: response });
        } catch (error) {
          log.debug(`saveToWorkflow(): asyncActionCreator(): failed: ${error}`);
          callback(error, null);
          dispatch({ type: SAVE_TO_WORKFLOW_FAIL, error: error });
        }
      };
    },

    getCanUserSaveToWorkflow: () => {
      log.debug('getCanUserSaveToWorkflow(): ');
      return (dispatch) => {
        log.debug('getCanUserSaveToWorkflow(): asyncActionCreator(): started');
        CLIENT.getCanUserSaveToWorkflow()
          .then((response) => {
            log.debug('getCanUserSaveToWorkflow(): asyncActionCreator(): ok');
            dispatch({ type: GET_CAN_USER_SAVE_TO_WORKFLOW_OK, response: response });
          })
          .catch((error) => {
            log.debug('getCanUserSaveToWorkflow: asyncActionCreator(): fail');
            dispatch({ type: GET_CAN_USER_SAVE_TO_WORKFLOW_FAIL, error: error });
          });
      };
    },

    doExportTransactions: (params) => {
      return (dispatch) => {
        dispatch({type: EXPORT_TRANSACTIONS});
        return CLIENT.exportTransactions(params)
          .then((response) => {
            log.debug('doExportTransactions success');
            dispatch({type: EXPORT_TRANSACTIONS_OK, response: response});
          })
          .catch((error) => {
            log.debug('doExportTransactions error');
            dispatch({type: EXPORT_TRANSACTIONS_FAIL, error});
          });
      };
    },

  doCloseVatBook: (requestBody) => {
    return (dispatch) => {
      dispatch({ type: CLOSE_VAT_BOOK });
      return CLIENT.closeVatBook(requestBody)
        .then((response) => {
          log.debug('doCloseVatBook success');
          dispatch({ type: CLOSE_VAT_BOOK_OK });
        })
        .catch((error) => {
          log.debug('doCloseVatBook error');
          dispatch({ type: CLOSE_VAT_BOOK_FAIL, error });
          return Promise.reject(error);
        });
    };
  },

  doCloseReturn: (requestBody) => {
    return async (dispatch) => {
      dispatch({ type: CLOSE_RETURN });
      try {
        const response = await CLIENT.closeReturn(requestBody);
        log.debug('doCloseReturn success');
        dispatch({ type: CLOSE_RETURN_OK });
        return response;
      } catch (error) {
        log.debug('doCloseReturn error');
        dispatch({ type: CLOSE_RETURN_FAIL, error });
        throw error;
      }
    };
  },

  // Non-dispatching helper: GET history non-empty ⇒ period already marked submitted.
  // Seeds REPORT_ALREADY_APPROVED on FilingPage (async Mark as submitted / refresh).
  checkReportAlreadySubmitted: (params) => {
    return () => {
      log.debug('checkReportAlreadySubmitted: params: ', params);
      return CLIENT.getClosedReturns(params)
        .then((response) => {
          const rows = [].concat(response || []);
          // A VAT-group parent's history query can also return closed rows belonging to its child companies,
          // so a non-empty array alone is not sufficient.
          const hasRecord = rows.some((row) =>
            String(row.companyId) === String(params.companyId) &&
            row.periodStartDate === params.startDate &&
            row.periodEndDate === params.endDate
          );
          log.debug('checkReportAlreadySubmitted: hasRecord: ', hasRecord);
          return hasRecord;
        })
        .catch((error) => {
          log.debug('checkReportAlreadySubmitted: failed: ', error);
          return false;
        });
    };
  },

  doLoadIntrastatCommercialSummary: (companyId, countryCode, startDate, endDate, intrastatReportType) => {
    return (dispatch) => {
      dispatch({ type: LOAD_INTRASTAT_COMMERCIAL_SUMMARY });
      return CLIENT.loadIntrastatCommercialSummary(companyId, countryCode, startDate, endDate, intrastatReportType)
        .then((response) => {
          log.debug('doLoadIntrastatCommercialSummary success');
          dispatch({ type: LOAD_INTRASTAT_COMMERCIAL_SUMMARY_OK, response: response });
        })
        .catch((error) => {
          log.debug('doLoadIntrastatCommercialSummary error');
          dispatch({ type: LOAD_INTRASTAT_COMMERCIAL_SUMMARY_FAIL, error });
        });
    };
  },

  /**
   * Initiates auto submission workflow for returns
   * This creates a Configuration job/task that will spawn widget tasks for processing
   */
  doInitiateAutoSubmission: (params, callback = () => { }) => {
    const startTime = Date.now();
    log.debug('doInitiateAutoSubmission(): ', params);
    return (dispatch) => {
      log.debug('doInitiateAutoSubmission(): asyncActionCreator(): started');
      dispatch({ type: INITIATE_AUTO_SUBMISSION });

      return CLIENT.initiateAutoSubmission(params)
        .then((response) => {
          const responseTime = Date.now() - startTime;
          log.debug('doInitiateAutoSubmission(): asyncActionCreator(): ok', response);
          log.debug('doInitiateAutoSubmission(): API response time:', responseTime + 'ms');

          const result = response?.data?.Configuration;

          if (result?.workflowId) {
            log.debug('doInitiateAutoSubmission(): Success! Workflow ID:', result.workflowId);
            log.setDictionaryMessage(ALL_MESSAGES.INITIATED_AUTO_SUBMISSION);
            callback(null, result);
            dispatch({ type: INITIATE_AUTO_SUBMISSION_OK, response: result });
          } else {
            log.debug('doInitiateAutoSubmission(): Failed:', result?.message);
            log.setDictionaryMessage(ALL_MESSAGES.AUTO_SUBMISSION_INITIATION_FAILED);
            const error = new Error(result?.message || 'Auto submission failed');
            callback(error, null);
            dispatch({ type: INITIATE_AUTO_SUBMISSION_FAIL, error });
          }
        })
        .catch((error) => {
          const responseTime = Date.now() - startTime;
          log.debug('doInitiateAutoSubmission(): asyncActionCreator(): failed', error);
          log.debug('doInitiateAutoSubmission(): API error time:', responseTime + 'ms');
          log.setDictionaryMessage(ALL_MESSAGES.AUTO_SUBMISSION_INITIATION_FAILED);
          callback(error, null);
          dispatch({ type: INITIATE_AUTO_SUBMISSION_FAIL, error });
          throw error;
        });
    };
  },

  /**
   * Checks whether a direct-submission workflow is already in progress for the
   * given company/period/return, so the Initiate button can be disabled.
   */
  checkWorkflowInProgress: (params) => {
    log.debug('checkWorkflowInProgress(): ', params);
    return (dispatch) => {
      log.debug('checkWorkflowInProgress(): asyncActionCreator(): started');
      return CLIENT.checkWorkflowInProgress(params)
        .then((response) => {
          log.debug('checkWorkflowInProgress(): asyncActionCreator(): ok', response);
          dispatch({ type: CHECK_WORKFLOW_IN_PROGRESS_OK, response: !!response?.inProgress });
        })
        .catch((error) => {
          log.debug('checkWorkflowInProgress(): asyncActionCreator(): failed', error);
          dispatch({ type: CHECK_WORKFLOW_IN_PROGRESS_FAIL, error });
        });
    };
  },
};

export default API;
